import { ChangeDetectionStrategy, Component, computed, contentChildren, input, output, signal, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { DrawerComponent } from 'common-ui';
import { EditableListAddRowDirective, EditableListHeaderDirective, EditableListRowDirective, EditableRowListViewComponent } from 'forms-ui';
import { ItemId } from 'products-data';
import { CustomLength } from '../../types';
import { WireCutCustomLengthComponent } from '../../wire-cut-custom-length';
import { ItemComponentBase } from 'products-ui';

@Component({
  selector: 'ideal-wire-cut-custom-length-selector-view',
  imports: [
    ReactiveFormsModule,
    EditableRowListViewComponent,
    EditableListRowDirective,
    EditableListAddRowDirective,
    EditableListHeaderDirective,
    WireCutCustomLengthComponent,
    MatButtonModule,
    MatIconModule,
    MatSortModule,
    DrawerComponent,
  ],
  templateUrl: './wire-cut-custom-length-selector-view.component.html',
  styleUrl: './wire-cut-custom-length-selector-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(paste)': 'onPaste($event)',
  },
})
export class WireCutCustomLengthSelectorViewComponent extends ItemComponentBase {
  // Inputs and outputs
  public readonly lengths = input<CustomLength[]>([]);
  public readonly lengthsChange = output<CustomLength[]>();
  // public readonly itemId = input<ItemId>();
  public readonly alwaysShowAddRowActions = input<boolean>(true);
  public readonly autoAddOnBlur = input<boolean>(false);

  private readonly _listComponent = viewChild.required(EditableRowListViewComponent<CustomLength>);
  private readonly _addRowComponent = viewChild<WireCutCustomLengthComponent>('addRowComponent');
  private readonly _rowComponents = contentChildren<WireCutCustomLengthComponent>('rowComponent');

  protected readonly focusedRowIndex = signal<number | null>(null);
  protected readonly addRowFocused = signal<boolean>(false);
  protected readonly showAddRowActions = computed(() => this.alwaysShowAddRowActions() || this.addRowFocused());

  private _rowHoverOpenTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
  private _rowHoverCloseTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
  private _addRowHoverOpenTimeout: ReturnType<typeof setTimeout> | null = null;
  private _addRowHoverCloseTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly HOVER_OPEN_DELAY_MS = 100;
  private readonly HOVER_CLOSE_DELAY_MS = 2000;

  protected readonly sortAccessor = (item: CustomLength, col: string): number => (col === 'length' ? item.length : item.quantity);

  protected readonly trackByLength = (item: CustomLength): number => item.length;

  protected findValidRowIndex(length: number): number | null {
    const list = this._listComponent();
    const values = list.formArray.value;
    const displayIndices = list.displayIndices();

    for (const arrayIndex of displayIndices) {
      const value = values[arrayIndex];
      if (value?.length === length) {
        return arrayIndex;
      }
    }
    return null;
  }

  protected getValidRowQuantity(index: number): number {
    const list = this._listComponent();
    const control = list.formArray.at(index);
    if (!control) return 0;

    const currentValue = control.value;
    if (!currentValue) return 0;

    const validIndex = this.findValidRowIndex(currentValue.length);
    if (validIndex === null || validIndex === index) {
      return currentValue.quantity;
    }

    const validControl = list.formArray.at(validIndex);
    return validControl?.value?.quantity ?? 0;
  }

  protected getMergedQuantity(index: number): number {
    const list = this._listComponent();
    const control = list.formArray.at(index);
    if (!control) return 0;

    const currentValue = control.value;
    if (!currentValue) return 0;

    const validIndex = this.findValidRowIndex(currentValue.length);
    if (validIndex === null || validIndex === index) {
      return currentValue.quantity;
    }

    const validControl = list.formArray.at(validIndex);
    const validQuantity = validControl?.value?.quantity ?? 0;
    return validQuantity + currentValue.quantity;
  }

  protected handleAdd(
    ctrl: FormControl<CustomLength | null>,
    list: EditableRowListViewComponent<CustomLength>,
    component: WireCutCustomLengthComponent,
  ): void {
    if (ctrl.valid && ctrl.value) {
      const newValue = { ...ctrl.value };
      list.append(newValue);
      ctrl.reset();
      component.reset();
      component.focusLength();
    }
  }

  protected handleMerge(
    currentIndex: number | FormControl<CustomLength | null>,
    list: EditableRowListViewComponent<CustomLength>,
    component?: WireCutCustomLengthComponent,
  ): void {
    let index: number;
    let ctrl: FormControl<CustomLength | null>;

    if (typeof currentIndex === 'number') {
      index = currentIndex;
      ctrl = list.formArray.at(index) as FormControl<CustomLength | null>;
    } else {
      ctrl = currentIndex;
      index = -1;
    }

    if (!ctrl.valid || !ctrl.value) return;

    const currentValue = ctrl.value;
    const validIndex = this.findValidRowIndex(currentValue.length);

    if (validIndex === null) {
      // No valid row found, shouldn't happen for conflicted rows, but handle gracefully
      return;
    }

    if (index === validIndex) {
      // Current row is the valid row, nothing to merge
      return;
    }

    const validValue = list.formArray.at(validIndex).value;
    if (!validValue) return;

    const newQuantity = validValue.quantity + currentValue.quantity;

    list.formArray.at(validIndex).setValue({ length: currentValue.length, quantity: newQuantity }, { emitEvent: false });

    if (index >= 0) {
      list.remove(index);
    }

    ctrl.reset();
    component?.reset();
    this.focusedRowIndex.set(null);
  }
  protected handleRemove(ctrl: FormControl<CustomLength | null>, list: EditableRowListViewComponent<CustomLength>): void {
    if (ctrl.valid && ctrl.value) {
      const idx = list.indexOf(ctrl.value);
      if (idx >= 0) {
        list.remove(idx);
        this.focusedRowIndex.set(null);
      }
    }
  }

  protected handleRowQuantityEnter(index: number): void {
    const rowComponents = this._rowComponents();
    const nextComponent = rowComponents[index + 1];
    const addRowComp = this._addRowComponent();

    if (nextComponent) {
      nextComponent.focusLength();
    } else if (addRowComp) {
      addRowComp.focusLength();
    }
  }

  protected handleAddRowQuantityEnter(): void {
    const addRowComp = this._addRowComponent();
    const listComp = this._listComponent();
    if (!addRowComp || !listComp) return;

    this.handleAdd(listComp.addControl, listComp, addRowComp);
  }

  protected handleRowFocusChange(index: number, isFocused: boolean): void {
    this.focusedRowIndex.set(isFocused ? index : null);
  }

  protected handleAddRowFocusChange(isFocused: boolean): void {
    this.addRowFocused.set(isFocused);
  }

  protected handleAddRowBlur(
    ctrl: FormControl<CustomLength | null>,
    list: EditableRowListViewComponent<CustomLength>,
    component: WireCutCustomLengthComponent,
  ): void {
    this.addRowFocused.set(false);
    if (this.autoAddOnBlur() && ctrl.valid && ctrl.value) {
      this.handleAdd(ctrl, list, component);
    }
  }

  protected onPaste(event: ClipboardEvent): void {
    const target = event.target as HTMLElement;
    const addRowComp = this._addRowComponent();

    if (!this.addRowFocused() || !addRowComp) return;

    const text = event.clipboardData?.getData('text') ?? '';
    if (!text.includes('\t') && !text.includes('\n')) return;

    const items = this._parsePastedItems(text);
    if (items.length === 0) return;

    event.preventDefault();

    const listComp = this._listComponent();
    if (listComp) {
      listComp.appendAll(items);
      listComp.addControl.reset();
      addRowComp.reset();
      addRowComp.focusLength();
    }
  }

  private _parsePastedItems(text: string): CustomLength[] {
    const lines = text.split(/\r?\n/);
    const results: CustomLength[] = [];
    let startIndex = 0;

    if (lines.length > 0 && lines[0].trim() !== '') {
      const firstLineCols = lines[0].split('\t');
      const firstCol = parseFloat(firstLineCols[0]?.trim() ?? '');
      const secondCol = parseFloat(firstLineCols[1]?.trim() ?? '');
      if (isNaN(firstCol) || isNaN(secondCol)) {
        startIndex = 1;
      }
    }

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === '') continue;

      const cols = line.split('\t');
      const lengthValue = parseFloat(cols[0]?.trim() ?? '');
      const quantityValue = parseFloat(cols[1]?.trim() ?? '');

      if (isNaN(lengthValue) || lengthValue < 1 || isNaN(quantityValue) || quantityValue < 1) {
        break;
      }

      const length = Math.ceil(lengthValue);
      const quantity = Math.ceil(quantityValue);

      results.push({ length, quantity });
    }

    return results;
  }
}
